/* =============================================================================
   Folha OIT 2000 — estrutura e dados

   Compartilhado entre a API (gera o PDF) e o front (o médico marca na tela).
   A estrutura mora aqui e em nenhum outro lugar: se a tela e o PDF tiverem
   listas próprias de símbolos ou de níveis de profusão, uma delas vai ficar
   para trás e o documento impresso deixa de ser o que o médico marcou.

   Sem dependências de propósito — os dois lados importam este arquivo direto.
   ========================================================================== */

export const QUALIDADE = ['1', '2', '3', '4'] as const;

/** Forma e tamanho das pequenas opacidades. A grade do papel é 3×2. */
export const FORMAS: readonly (readonly string[])[] = [
  ['p', 's'],
  ['q', 't'],
  ['r', 'u'],
];

/** Zonas do pulmão, de cima para baixo. Cada lado é marcado em separado. */
export const ZONAS = ['superior', 'media', 'inferior'] as const;
export const LADOS = ['D', 'E'] as const;

/** Profusão: 4 linhas × 3 colunas, na ordem exata do formulário. */
export const PROFUSAO: readonly (readonly string[])[] = [
  ['0/-', '0/0', '0/1'],
  ['1/0', '1/1', '1/2'],
  ['2/1', '2/2', '2/3'],
  ['3/2', '3/3', '3/+'],
];

export const GRANDES_OPACIDADES = ['O', 'A', 'B', 'C'] as const;

/** Linhas das grades de placas pleurais (3B). */
export const LOCAIS_PLACA = [
  { chave: 'paredePerfil', rotulo: 'Parede em perfil' },
  { chave: 'frontal', rotulo: 'Frontal' },
  { chave: 'diafragma', rotulo: 'Diafragma' },
  { chave: 'outros', rotulo: 'Outros Locais' },
] as const;

/** Linhas da grade de espessamento difuso (3D) — não tem diafragma. */
export const LOCAIS_DIFUSO = [
  { chave: 'paredePerfil', rotulo: 'Parede em perfil' },
  { chave: 'frontal', rotulo: 'Frontal' },
] as const;

export const LOCAL_OPCOES = ['0', 'D', 'E'] as const;
export const EXTENSAO_OPCOES = ['0', '1', '2', '3'] as const;
export const LARGURA_OPCOES = ['a', 'b', 'c'] as const;

export const LEGENDA_EXTENSAO = [
  'Até 1/4 da parede lateral = 1',
  '1/4 a ½ da parede lateral = 2',
  '> ½ da parede lateral. = 3',
];
export const LEGENDA_LARGURA = ['3 a 5 mm = a', '5 a 10 mm = b', '> 10 mm = c'];

/** Símbolos da seção 4B, na ordem do formulário. `od` exige comentário. */
export const SIMBOLOS = [
  'aa', 'at', 'ax', 'bu', 'ca', 'cg', 'cn', 'co', 'cp', 'cv',
  'di', 'ef', 'em', 'es', 'fr', 'hi', 'ho', 'id', 'ih', 'kl',
  'me', 'pa', 'pb', 'pi', 'px', 'ra', 'rp', 'tb', 'od',
] as const;

/* -------------------------------------------------------------- os dados */

export type Lado = (typeof LADOS)[number];
export type PorLado<T> = { D: T; E: T };

export interface DadosOit {
  qualidadeTecnica: string | null;
  qualidadeComentario: string;

  radiografiaNormal: boolean | null;

  parenquimaAnormal: boolean | null;
  formaPrimaria: string | null;
  formaSecundaria: string | null;
  /** zonas[lado][índice da zona] */
  zonas: PorLado<boolean[]>;
  profusao: string | null;
  grandesOpacidades: string | null;

  pleuralAnormal: boolean | null;

  placas: boolean | null;
  placasLocal: Record<string, string | null>;
  placasCalcificacao: Record<string, string | null>;
  placasExtensao: PorLado<string | null>;
  placasLargura: PorLado<string | null>;

  obliteracaoSeio: string | null;

  difuso: boolean | null;
  difusoCalcificacao: Record<string, string | null>;
  difusoExtensao: PorLado<string | null>;
  difusoLargura: PorLado<string | null>;

  outrasAnormalidades: boolean | null;
  simbolos: string[];
  comentarios: string;
}

export function oitVazio(): DadosOit {
  const porLocal = (locais: readonly { chave: string }[]) =>
    Object.fromEntries(locais.map((l) => [l.chave, null])) as Record<string, string | null>;

  return {
    qualidadeTecnica: null,
    qualidadeComentario: '',
    radiografiaNormal: null,
    parenquimaAnormal: null,
    formaPrimaria: null,
    formaSecundaria: null,
    zonas: { D: [false, false, false], E: [false, false, false] },
    profusao: null,
    grandesOpacidades: null,
    pleuralAnormal: null,
    placas: null,
    placasLocal: porLocal(LOCAIS_PLACA),
    placasCalcificacao: porLocal(LOCAIS_PLACA),
    placasExtensao: { D: null, E: null },
    placasLargura: { D: null, E: null },
    obliteracaoSeio: null,
    difuso: null,
    difusoCalcificacao: porLocal(LOCAIS_DIFUSO),
    difusoExtensao: { D: null, E: null },
    difusoLargura: { D: null, E: null },
    outrasAnormalidades: null,
    simbolos: [],
    comentarios: '',
  };
}

/** Preenche o que faltar, para dados gravados antes de um campo existir. */
export function normalizarOit(bruto: unknown): DadosOit {
  const v = (bruto ?? {}) as Partial<DadosOit>;
  const base = oitVazio();
  return {
    ...base,
    ...v,
    zonas: {
      D: v.zonas?.D ?? base.zonas.D,
      E: v.zonas?.E ?? base.zonas.E,
    },
    placasLocal: { ...base.placasLocal, ...v.placasLocal },
    placasCalcificacao: { ...base.placasCalcificacao, ...v.placasCalcificacao },
    placasExtensao: { ...base.placasExtensao, ...v.placasExtensao },
    placasLargura: { ...base.placasLargura, ...v.placasLargura },
    difusoCalcificacao: { ...base.difusoCalcificacao, ...v.difusoCalcificacao },
    difusoExtensao: { ...base.difusoExtensao, ...v.difusoExtensao },
    difusoLargura: { ...base.difusoLargura, ...v.difusoLargura },
    simbolos: v.simbolos ?? [],
  };
}

/** O que ainda falta para o laudo poder ser assinado.
 *
 *  O formulário tem caminhos: radiografia normal encerra a leitura, e cada
 *  "NÃO" pula um bloco inteiro. Validar tudo sempre obrigaria o médico a
 *  preencher seções que o próprio formulário manda pular. */
export function pendenciasOit(d: DadosOit): string[] {
  const faltas: string[] = [];

  if (!d.qualidadeTecnica) faltas.push('1A — marque a qualidade técnica.');
  if (d.qualidadeTecnica && d.qualidadeTecnica !== '1' && !d.qualidadeComentario.trim()) {
    faltas.push('1A — qualidade diferente de 1 exige comentário.');
  }
  if (d.radiografiaNormal === null) faltas.push('1B — diga se a radiografia é normal.');
  if (d.radiografiaNormal) return faltas; // normal encerra a leitura

  if (d.parenquimaAnormal === null) faltas.push('2A — responda sobre o parênquima.');
  if (d.parenquimaAnormal) {
    if (!d.formaPrimaria) faltas.push('2B — marque a forma/tamanho primária.');
    if (!d.formaSecundaria) faltas.push('2B — marque a forma/tamanho secundária.');
    if (!d.profusao) faltas.push('2B — marque a profusão.');
    if (![...d.zonas.D, ...d.zonas.E].some(Boolean)) faltas.push('2B — marque ao menos uma zona.');
    if (!d.grandesOpacidades) faltas.push('2C — marque as grandes opacidades.');
  }

  if (d.pleuralAnormal === null) faltas.push('3A — responda sobre a pleura.');
  if (d.pleuralAnormal) {
    if (d.placas === null) faltas.push('3B — diga se há placas pleurais.');
    if (d.difuso === null) faltas.push('3D — diga se há espessamento pleural difuso.');
  }

  if (d.outrasAnormalidades === null) faltas.push('4A — responda sobre outras anormalidades.');
  if (d.outrasAnormalidades && d.simbolos.length === 0) {
    faltas.push('4B — marque ao menos um símbolo.');
  }
  if (d.simbolos.includes('od') && !d.comentarios.trim()) {
    faltas.push('4C — o símbolo "od" exige um comentário.');
  }

  return faltas;
}
